import * as React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { AddRounded } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Discounts, ProviderCommissionType } from '@prisma/client';
import {
  CheckoutFormValues,
  SelectedAppointment,
  TipState,
  tipSchema,
} from '../../types/appointments';
import { FormData } from '../../components/global/FormSmallModal';
import {
  isCashOrDigitalMethod,
  isDiscountAllowed,
  isFreeWithDiscount,
  isTipDisplayed,
  getProviderCommissionOptions,
} from '../../helpers/payments';
import {
  SelectDiscountPayload,
  selectDiscountSchema,
} from '../../types/discounts';
import useGetDiscounts from '../discounts/useGetDiscounts';
import {
  convertDiscountAmountToDisplay,
  getDiscountLabel,
} from '../../helpers/discounts';
import useUser from '../global/useUser';
import {
  clearCheckoutAppointment,
  getServiceName,
  isAppointmentDepositPaid,
  isCheckoutProviderChangeLocked,
  isDepositAllowed,
} from '../../helpers/appointments';
import { Appointment } from '../../interfaces';
import useGetCheckoutData, {
  getQuotedPaymentMethod,
} from './useGetCheckoutData';
import useCashDiscount from '../payments/useCashDiscount';
import { AdditionalServiceCheckoutItem } from '../../types/payments';
import { CheckoutFormItem } from '../../components/form/CheckoutFormItems';
import { CheckoutSummaryItem } from '../../types/checkouts';
import { formatDollarAmount } from '../../utils/global/helper';
import {
  getAppointmentById,
  invalidateAppointments,
  invalidateDepositTransferAppointments,
} from '../../services/appointments';
import useRemoveAppointmentDeposit from '../appointments/useRemoveAppointmentDeposit';
import useConfirm from '../useConfirm';
import useBusinessWebRedesign from '../businesses/useBusinessWebRedesign';
import { useServiceFormModal } from './useServiceFormModal';
import { useItemFormModal } from './useItemFormModal';
import useCheckoutTip from './useCheckoutTip';
import useCheckoutStep from './useCheckoutStep';

export type AddItemsTab = 'services' | 'items' | 'giftCards';

interface Args {
  checkoutData: ReturnType<typeof useGetCheckoutData>;
  selectedDiscount?: Discounts | null;
  appointment: SelectedAppointment | null;
  /** The appointment the checkout was opened with, if any; it cannot be removed. */
  selectedAppointment: SelectedAppointment | null;
  values: CheckoutFormValues;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) => void;
  setAppointment: React.Dispatch<
    React.SetStateAction<SelectedAppointment | null>
  >;
  onRefreshAppointment: () => void | Promise<void>;
}

/**
 * Owns everything the checkout cart needs: the editable line items and the
 * order summary rows. Both layouts render from this one result, so the rows
 * and their calculations cannot drift between them.
 */
export default function useCheckoutPricing({
  checkoutData: {
    data: checkoutData,
    isFetching,
    isDebouncing,
    isPlaceholderData,
  },
  selectedDiscount,
  appointment,
  selectedAppointment,
  values,
  setFieldValue,
  setAppointment,
  onRefreshAppointment,
}: Args) {
  const [isDepositTransferModalOpen, setIsDepositTransferModalOpen] =
    React.useState(false);
  // null is closed; an open request carries the tab to land on, if any.
  const [addItems, setAddItems] = React.useState<{
    tab?: AddItemsTab;
  } | null>(null);
  const [form, setForm] = React.useState<FormData | null>(null);
  const [isTipModalOpen, setIsTipModalOpen] = React.useState(false);

  const { active: redesignActive } = useBusinessWebRedesign();
  const { step } = useCheckoutStep();
  const confirm = useConfirm();
  const { user } = useUser();
  const showServiceForm = useServiceFormModal();
  const showItemForm = useItemFormModal();
  const { getAllInPrice, cashDiscountEnabled: businessCashDiscountEnabled } =
    useCashDiscount();

  const { data: discounts = [] } = useGetDiscounts({
    activeOnly: true,
  });

  const customCheckoutItems = React.useMemo(
    () => values.customOrderItems ?? [],
    [values.customOrderItems],
  );
  const isLoading = isFetching || isDebouncing || isPlaceholderData;
  const {
    appointment: apptData,
    order: orderData,
    feeCents,
    totalPriceCents,
    hasTaxRate,
    discountAmountCents,
    giftCardAmountCents,
    isTotalCoveredByGiftCard,
    depositCashDiscountCents = 0,
    depositBundledFeeCents = 0,
    cashDiscountCents = 0,
    cashDiscountEnabled: responseCashDiscountEnabled,
  } = checkoutData ?? {};
  const cashDiscountEnabled =
    responseCashDiscountEnabled ?? businessCashDiscountEnabled;
  const price =
    ((apptData?.originalPriceCents || 0) +
      (orderData?.originalPriceCents || 0)) /
    100;
  const depositAmountPaid = apptData?.depositAmountPaidCents
    ? apptData.depositAmountPaidCents / 100
    : 0;
  const depositCashDiscount = depositCashDiscountCents / 100;
  const cashDiscount = cashDiscountCents / 100;
  // Bundled fee only surfaces here for a paid, fee-bearing deposit; feeless
  // shows it as Cash Discount Applied, unpaid contributes nothing.
  const depositPaidVisible =
    depositAmountPaid > 0
      ? depositAmountPaid +
        (depositCashDiscount > 0 ? 0 : depositBundledFeeCents / 100)
      : 0;
  const serviceSalesTax = (apptData?.salesTaxCents || 0) / 100;
  const productSalesTax = (orderData?.salesTaxCents || 0) / 100;
  const customerServiceFee = (feeCents || 0) / 100;
  const totalDue = (totalPriceCents || 0) / 100;
  const isFreeEditAllowed = isFreeWithDiscount(
    values.paymentMethod,
    selectedDiscount,
  );
  const discountAmount = (discountAmountCents || 0) / 100;
  const giftCardAmount = (giftCardAmountCents || 0) / 100;
  const subtotal = Math.max(0, price - cashDiscount);
  // A percentage tip has no amount in the form — the estimate resolves it.
  const resolvedTip =
    values.tipAmt ||
    ((apptData?.tipCents ?? 0) + (orderData?.tipCents ?? 0)) / 100;
  const isCashOrDigital = isCashOrDigitalMethod(values.paymentMethod);
  const showSalesTax = !isCashOrDigital || values.isCashTaxed;
  const serviceTaxRate = `${(
    (user?.business?.businessTaxRates?.serviceTaxRate || 0) * 100
  ).toFixed(2)}%`;
  const productTaxRate = `${(
    (user?.business?.businessTaxRates?.productTaxRate || 0) * 100
  ).toFixed(2)}%`;

  const isTipVisible = isTipDisplayed({
    values,
    appointment,
    user,
  });

  const refreshAppointment = async () => {
    if (!appointment) return;
    await onRefreshAppointment();
    const updatedAppointment: Appointment | null = await getAppointmentById(
      appointment.appointmentId,
    ).catch(() => null);

    if (
      updatedAppointment &&
      (updatedAppointment.depositPrice ?? 0) > values.price
    ) {
      setFieldValue('price', updatedAppointment.price ?? 0);
    }

    setAppointment(updatedAppointment);
    invalidateAppointments();
    invalidateDepositTransferAppointments();
  };

  const { mutateAsync: removeDeposit } = useRemoveAppointmentDeposit({
    onSuccess: () => {
      refreshAppointment();
    },
    onError: () => {
      toast.error('Failed to remove deposit');
    },
  });

  const discountForm: FormData<SelectDiscountPayload> = {
    title: 'Select discount',
    destructiveAction: selectedDiscount
      ? {
          label: 'Remove discount',
          onClick: () => setFieldValue('discountId', undefined),
        }
      : undefined,
    initialValues: {
      discountId: selectedDiscount?.id || '',
    } as SelectDiscountPayload,
    validationSchema: selectDiscountSchema,
    inputRows: [
      [
        {
          inputType: 'select',
          name: 'discountId',
          options: discounts.map(discount => ({
            value: discount.id,
            label: getDiscountLabel(discount),
          })),
          autoWidth: false,
          placeholder: 'Select discount',
          label: 'Discount',
        },
      ],
    ],
    onSubmit: async newValues => {
      setFieldValue('discountId', newValues.discountId);
      setForm(null);
    },
    submitButtonText: 'Apply',
  };

  const handleRemoveItem = React.useCallback(
    (id: string) => {
      setFieldValue(
        'customOrderItems',
        customCheckoutItems?.filter(item => item.id !== id),
      );
    },
    [customCheckoutItems, setFieldValue],
  );

  const additionalServicesList = React.useMemo(
    () => values.additionalServices ?? [],
    [values.additionalServices],
  );

  // Per-line fee-base contributions used for the group-fee proportional
  // split (so the $4 min applies to the whole cart, not each line).
  const cartFeeBases = React.useMemo(() => {
    const mainBase = appointment
      ? Math.max(0, (values.price ?? 0) - depositAmountPaid)
      : 0;
    const additionalBases = additionalServicesList.map(s => s.price);
    const itemBases = customCheckoutItems.map(i => i.price * (i.quantity ?? 1));
    const total =
      mainBase +
      additionalBases.reduce((a, b) => a + b, 0) +
      itemBases.reduce((a, b) => a + b, 0);
    return { mainBase, additionalBases, itemBases, total };
  }, [
    appointment,
    values.price,
    depositAmountPaid,
    additionalServicesList,
    customCheckoutItems,
  ]);

  // Only an appointment picked inside the checkout can be dropped; one the
  // checkout was opened for is what the checkout is.
  const handleRemoveAppointment = React.useCallback(
    () =>
      clearCheckoutAppointment({
        paymentMethod: values.paymentMethod,
        setFieldValue,
        onSelectAppointment: setAppointment,
      }),
    [values.paymentMethod, setFieldValue, setAppointment],
  );

  const handleRemoveAdditionalService = React.useCallback(
    (id: string) => {
      setFieldValue(
        'additionalServices',
        additionalServicesList.filter(s => s.id !== id),
      );
    },
    [additionalServicesList, setFieldValue],
  );

  const serviceName = appointment ? getServiceName(appointment, user) : '';

  const getEmployeeName = React.useCallback(
    (employeeId: string | null | undefined): string | undefined => {
      if (!employeeId) return undefined;
      const emp = user?.business?.employees?.find(
        e => e.employeeId === employeeId,
      );
      return emp?.displayName ?? undefined;
    },
    [user],
  );

  const getServiceCommissionRateLabel = React.useCallback(
    (
      employeeId: string | null | undefined,
      serviceId: number | null | undefined,
      commissionType: ProviderCommissionType | null | undefined,
    ): string | undefined => {
      if (!employeeId || !user?.business) return undefined;
      const options = getProviderCommissionOptions(
        user.business.employees,
        user.business.services,
        employeeId,
        serviceId,
      );
      if (options.length <= 1) return undefined;
      const selected =
        options.find(o => o.type === commissionType) ??
        options.find(o => o.type === ProviderCommissionType.BASE);
      if (!selected) return undefined;
      return `${(selected.rate * 100).toFixed(0)}%`;
    },
    [user],
  );

  const handleEditAppointmentService = React.useCallback(async () => {
    if (!appointment || (values.paymentMethod === 'FREE' && !isFreeEditAllowed))
      return;
    const depositMinimum = isAppointmentDepositPaid(appointment)
      ? appointment.depositPrice ?? 0
      : 0;
    const service =
      user?.business?.services?.find(
        s => s.serviceId === (appointment as any).serviceId,
      ) ?? null;
    const disableProviderSelect = isCheckoutProviderChangeLocked(
      appointment,
      values.employeeId,
      user,
    );
    const result = await showServiceForm({
      service: service ?? {
        serviceId: (appointment as any).serviceId ?? 0,
        serviceName,
        serviceCategory: 'TATTOO',
        serviceDescription: '',
        defaultDurationInMinutes: 0,
        defaultPrice: values.price,
        defaultDepositPrice: null,
        enableDirectBookings: false,
        minimumAgeInMonths: 0,
        employeesToServices: [],
      },
      user,
      appointmentEmployeeId: values.employeeId,
      initialValues: {
        price: values.price,
        employeeId: values.employeeId,
        providerCommissionType:
          values.providerCommissionType ?? ProviderCommissionType.BASE,
      },
      minPrice: depositMinimum > 0 ? depositMinimum : undefined,
      title: `Edit ${serviceName}`,
      submitButtonText: 'Save',
      disableProviderSelect,
      depositPrice: depositAmountPaid,
      paymentMethod: values.paymentMethod,
      depositPaymentMethod: appointment?.depositPaymentMethod,
      depositBundledFeeCents,
      groupOtherLinesTotal: cartFeeBases.total - cartFeeBases.mainBase,
      onRemove:
        redesignActive && !selectedAppointment
          ? handleRemoveAppointment
          : undefined,
      removeLabel: 'Remove appointment',
    });
    if (!result) return;
    setFieldValue('price', Number(result.price) || 0);
    if (result.providerCommissionType) {
      setFieldValue('providerCommissionType', result.providerCommissionType);
    }
    if (result.employeeId) {
      setFieldValue('employeeId', result.employeeId);
    }
  }, [
    appointment,
    user,
    serviceName,
    values.price,
    values.employeeId,
    values.providerCommissionType,
    values.paymentMethod,
    isFreeEditAllowed,
    showServiceForm,
    setFieldValue,
    depositAmountPaid,
    depositBundledFeeCents,
    cartFeeBases,
    redesignActive,
    selectedAppointment,
    handleRemoveAppointment,
  ]);

  const handleEditAdditionalService = React.useCallback(
    async (svc: AdditionalServiceCheckoutItem) => {
      const service =
        user?.business?.services?.find(s => s.serviceId === svc.serviceId) ??
        null;
      const idx = additionalServicesList.findIndex(s => s.id === svc.id);
      const thisLineBase = idx >= 0 ? cartFeeBases.additionalBases[idx] : 0;
      const result = await showServiceForm({
        service: service ?? {
          serviceId: svc.serviceId ?? 0,
          serviceName: svc.serviceName,
          serviceCategory: 'TATTOO',
          serviceDescription: '',
          defaultDurationInMinutes: 0,
          defaultPrice: svc.price,
          defaultDepositPrice: null,
          enableDirectBookings: false,
          minimumAgeInMonths: 0,
          employeesToServices: [],
        },
        user,
        appointmentEmployeeId: svc.employeeId,
        initialValues: {
          price: svc.price,
          employeeId: svc.employeeId,
          providerCommissionType: svc.providerCommissionType,
        },
        title: `Edit ${svc.serviceName}`,
        submitButtonText: 'Save',
        paymentMethod: values.paymentMethod,
        groupOtherLinesTotal: cartFeeBases.total - thisLineBase,
        onRemove: redesignActive
          ? () => handleRemoveAdditionalService(svc.id)
          : undefined,
      });
      if (!result) return;
      setFieldValue(
        'additionalServices',
        additionalServicesList.map(s =>
          s.id === svc.id
            ? {
                ...s,
                price: Number(result.price) || 0,
                employeeId: result.employeeId || svc.employeeId,
                providerCommissionType:
                  result.providerCommissionType ?? svc.providerCommissionType,
              }
            : s,
        ),
      );
    },
    [
      user,
      showServiceForm,
      additionalServicesList,
      setFieldValue,
      values.paymentMethod,
      cartFeeBases,
      redesignActive,
      handleRemoveAdditionalService,
    ],
  );

  const handleEditOrderItem = React.useCallback(
    async (itemId: string) => {
      const item = customCheckoutItems.find(i => i.id === itemId);
      if (!item) return;
      const idx = customCheckoutItems.findIndex(i => i.id === itemId);
      const thisLineBase = idx >= 0 ? cartFeeBases.itemBases[idx] : 0;
      const result = await showItemForm({
        name: item.name,
        initialPrice: item.price,
        initialQuantity: item.quantity,
        title: `Edit ${item.name}`,
        submitButtonText: 'Save',
        paymentMethod: values.paymentMethod,
        groupOtherLinesTotal: cartFeeBases.total - thisLineBase,
        onRemove: redesignActive ? () => handleRemoveItem(item.id) : undefined,
      });
      if (!result) return;
      setFieldValue(
        'customOrderItems',
        customCheckoutItems.map(i =>
          i.id === itemId
            ? {
                ...i,
                price: Number(result.price) ?? i.price,
                quantity: Number(result.quantity) ?? i.quantity,
              }
            : i,
        ),
      );
    },
    [
      customCheckoutItems,
      showItemForm,
      setFieldValue,
      values.paymentMethod,
      cartFeeBases,
      redesignActive,
      handleRemoveItem,
    ],
  );

  const items = React.useMemo(() => {
    const tempItems: CheckoutFormItem[] = [];
    const {
      mainBase,
      additionalBases,
      itemBases,
      total: totalCartBase,
    } = cartFeeBases;
    if (appointment) {
      tempItems.push({
        type: 'service',
        id: appointment.appointmentId,
        disabled: values.paymentMethod === 'FREE' && !isFreeEditAllowed,
        name: serviceName,
        provider: getEmployeeName(values.employeeId),
        duration: appointment.appointmentLengthInMinutes ?? undefined,
        commissionRate: getServiceCommissionRateLabel(
          values.employeeId,
          appointment.serviceId,
          values.providerCommissionType,
        ),
        price: getAllInPrice(values.price, {
          depositPrice: depositAmountPaid,
          paymentMethod: values.paymentMethod,
          depositPaymentMethod: appointment?.depositPaymentMethod,
          depositBundledFeeCents,
          groupOtherLinesTotal: totalCartBase - mainBase,
        }),
        onClick:
          values.paymentMethod !== 'FREE' || isFreeEditAllowed
            ? handleEditAppointmentService
            : undefined,
      });
    }
    additionalServicesList.forEach((addSvc, idx) => {
      tempItems.push({
        type: 'service',
        id: addSvc.id,
        disabled: values.paymentMethod === 'FREE' && !isFreeEditAllowed,
        name: addSvc.serviceName,
        provider: getEmployeeName(addSvc.employeeId),
        commissionRate: getServiceCommissionRateLabel(
          addSvc.employeeId,
          addSvc.serviceId,
          addSvc.providerCommissionType,
        ),
        price: getAllInPrice(addSvc.price, {
          paymentMethod: values.paymentMethod,
          groupOtherLinesTotal: totalCartBase - additionalBases[idx],
        }),
        onClick:
          values.paymentMethod !== 'FREE' || isFreeEditAllowed
            ? () => handleEditAdditionalService(addSvc)
            : undefined,
        onRemove: () => handleRemoveAdditionalService(addSvc.id),
      });
    });
    customCheckoutItems.forEach((i, idx) => {
      tempItems.push({
        type: 'item',
        id: i.id,
        name: i.name,
        price: getAllInPrice(i.price, {
          paymentMethod: values.paymentMethod,
          quantity: i.quantity,
          groupOtherLinesTotal: totalCartBase - itemBases[idx],
        }),
        quantity: i.quantity,
        onClick: () => handleEditOrderItem(i.id),
        onRemove: () => handleRemoveItem(i.id),
      });
    });
    return tempItems;
  }, [
    appointment,
    additionalServicesList,
    customCheckoutItems,
    handleRemoveItem,
    handleRemoveAdditionalService,
    handleEditAppointmentService,
    handleEditAdditionalService,
    handleEditOrderItem,
    serviceName,
    getEmployeeName,
    getServiceCommissionRateLabel,
    values.paymentMethod,
    values.price,
    values.employeeId,
    values.providerCommissionType,
    isFreeEditAllowed,
    getAllInPrice,
    depositAmountPaid,
    depositBundledFeeCents,
    cartFeeBases,
  ]);

  // The API tips on the whole service, so a paid deposit counts — and its
  // bundled fee counts only when the method being quoted bears fees.
  const isQuotedCashLike = isCashOrDigitalMethod(
    getQuotedPaymentMethod(values.paymentMethod),
  );
  const tipBase =
    subtotal +
    (depositAmountPaid > 0
      ? depositAmountPaid +
        (isQuotedCashLike ? 0 : depositBundledFeeCents / 100)
      : 0);

  // The API splits a tip evenly across every provider on the payment, so the
  // modal has to name all of them, not just the appointment's.
  const tipProviders = Array.from(
    new Set([
      values.employeeId,
      ...additionalServicesList.map(service => service.employeeId),
    ]),
  )
    .map(getEmployeeName)
    .filter((name): name is string => !!name);

  const tip = useCheckoutTip({
    amount: resolvedTip,
    percent: values.tipPercentage,
    setFieldValue,
  });

  const tipForm: FormData<TipState> = {
    title: 'Enter tip amount',
    validationSchema: tipSchema,
    initialValues: { tipAmt: null } as TipState,
    inputRows: [
      [
        {
          inputType: 'number',
          label: 'Tip Amount',
          name: 'tipAmt',
        },
      ],
    ],
    onSubmit: async tipVal => {
      tip.set({
        amount: tipVal.tipAmt ? Number(tipVal.tipAmt) : 0,
        percent: null,
      });
      setForm(null);
    },
  };

  const preSubtotalItems: CheckoutSummaryItem[] = [
    {
      label: 'Price',
      value:
        depositAmountPaid > 0
          ? price + depositAmountPaid + depositBundledFeeCents / 100
          : price,
    },
    {
      label: 'Deposit',
      value: depositPaidVisible,
      isPaid: !!depositAmountPaid,
      subtractFromSubtotal: !!depositAmountPaid,
      visible: !!appointment,
      floatingButton:
        !!appointment &&
        isDepositAllowed(appointment) &&
        ((!appointment.businessId && appointment.employeeId === user?.userId) ||
          appointment.businessId === user?.business?.id)
          ? {
              icon: depositAmountPaid ? DeleteIcon : AddRounded,
              onClick: async () => {
                if (!appointment) return;
                if (depositAmountPaid) {
                  const confirmed = await confirm({
                    title: 'Remove Deposit',
                    message:
                      "Are you sure you want to remove the deposit? This deposit will be moved to the clients profile to use for a future appointment. If you're looking to entirely remove the deposit, you should Void or Refund the deposit transaction instead",
                    negativeText: 'Cancel',
                    positiveText: 'Remove Deposit',
                  });
                  if (!confirmed) return;

                  removeDeposit(appointment.appointmentId);
                } else {
                  setIsDepositTransferModalOpen(true);
                }
              },
              showWithNoValue: true,
            }
          : undefined,
    },
    {
      label: 'Cash Discount Applied',
      value: -depositCashDiscount,
      visible: depositCashDiscount > 0,
    },
    {
      label: 'Cash Discount',
      value: -cashDiscount,
      visible: cashDiscount > 0,
    },
  ];

  const postSubtotalItems: CheckoutSummaryItem[] = [
    {
      label: selectedDiscount
        ? `Discount - ${getDiscountLabel(selectedDiscount)}`
        : 'Add discount',
      value: -discountAmount,
      visible:
        (!values.paymentMethod || isDiscountAllowed(values.paymentMethod)) &&
        (!redesignActive || !!discountAmount),
      cellActionWhenEmpty: redesignActive
        ? undefined
        : {
            label: 'Add discount',
            onClick: () => setForm(discountForm),
          },
      floatingButton: {
        icon: DeleteIcon,
        onClick: () => setFieldValue('discountId', undefined),
      },
    },
    {
      // Naming the percentage keeps the row honest about why it moves when
      // the cart does.
      label:
        tip.percent != null
          ? `Tip added (${Math.round(tip.percent * 100)}%)`
          : 'Tip added',
      value: tip.amount,
      visible: !!isTipVisible && !!tip.amount,
      floatingButton: {
        icon: DeleteIcon,
        onClick: tip.clear,
      },
    },
    {
      label: 'Add tip',
      value: 0,
      cellActionWhenEmpty: {
        label: 'Add tip',
        onClick: () =>
          redesignActive ? setIsTipModalOpen(true) : setForm(tipForm),
      },
      visible:
        !!isTipVisible &&
        !tip.amount &&
        (!redesignActive || step === 'payment'),
    },
    {
      label: 'Add sales tax',
      value: 0,
      cellActionWhenEmpty: {
        label: 'Add sales tax',
        onClick: () => setFieldValue('isCashTaxed', true),
      },
      // hasTaxRate is undefined until the first response, and an undefined
      // `visible` reads as visible — so the row would offer a tax there is no
      // rate for, and clicking it could never hide it again.
      visible: !!hasTaxRate && isCashOrDigital && !values.isCashTaxed,
    },
    {
      label: `Service sales tax (${serviceTaxRate})`,
      value: serviceSalesTax,
      floatingButton:
        isCashOrDigital && values.isCashTaxed
          ? {
              icon: DeleteIcon,
              onClick: () => setFieldValue('isCashTaxed', false),
            }
          : undefined,
      visible: !!serviceSalesTax && showSalesTax,
    },
    {
      label: `Product sales tax (${productTaxRate})`,
      value: productSalesTax,
      floatingButton:
        isCashOrDigital && values.isCashTaxed
          ? {
              icon: DeleteIcon,
              onClick: () => setFieldValue('isCashTaxed', false),
            }
          : undefined,
      visible: !!productSalesTax && showSalesTax,
    },
    {
      label: 'Convenience fee',
      value: customerServiceFee,
      // Under cash discount the fee is already baked into the Price line.
      visible: !cashDiscountEnabled && !!customerServiceFee,
    },
  ];

  const canEditItems = values.paymentMethod !== 'FREE' || isFreeEditAllowed;

  return {
    items,
    itemsTitle: appointment ? 'Services and items' : 'Transaction Details',
    canEditItems,
    searchItemsLabel: appointment
      ? 'Search services & items to add'
      : 'Search items to add',
    hasAppointment: !!appointment,
    // Shown from the moment it is picked: on an empty cart it is worth $0, and
    // hiding it would leave the discount applied with nothing saying so.
    discountLine: selectedDiscount
      ? {
          id: selectedDiscount.id,
          name: selectedDiscount.name,
          meta:
            selectedDiscount.type === 'PERCENTAGE'
              ? `${convertDiscountAmountToDisplay(selectedDiscount)}% off`
              : `${formatDollarAmount(
                  convertDiscountAmountToDisplay(selectedDiscount),
                )} off`,
          amount: -discountAmount,
          onClick: () => setForm(discountForm),
        }
      : undefined,
    isTipAvailable: isTipVisible,
    depositNote:
      depositPaidVisible > 0
        ? `Deposit of ${formatDollarAmount(depositPaidVisible)} already applied`
        : undefined,
    summary: {
      preSubtotalItems,
      postSubtotalItems,
      subtotal,
      total:
        totalDue === 0 && isTotalCoveredByGiftCard && giftCardAmount > 0
          ? giftCardAmount
          : totalDue ?? 0,
      isLoading,
      minimumPrice: {
        value: appointment?.depositPrice ?? 0,
        errorVisible:
          (!totalDue || totalDue <= 0) &&
          values.paymentMethod !== 'FREE' &&
          !isTotalCoveredByGiftCard,
      },
    },
    isDiscountAvailable:
      !!discounts.length &&
      (!values.paymentMethod || isDiscountAllowed(values.paymentMethod)),
    hasDiscount: !!selectedDiscount,
    openAddItems: (tab?: AddItemsTab) => setAddItems({ tab }),
    openDiscountForm: () => setForm(discountForm),
    modals: {
      appointment,
      services: user?.business?.services,
      addItems,
      closeAddItems: () => setAddItems(null),
      form,
      closeForm: () => setForm(null),
      isDepositTransferModalOpen,
      closeDepositTransfer: () => setIsDepositTransferModalOpen(false),
      refreshAppointment,
      tip: {
        open: isTipModalOpen,
        close: () => setIsTipModalOpen(false),
        base: tipBase,
        amount: tip.amount,
        percent: tip.percent,
        providers: tipProviders,
        apply: tip.set,
      },
    },
  };
}

export type CheckoutPricing = ReturnType<typeof useCheckoutPricing>;
